'use client';

import { FieldValues, Path, useController } from 'react-hook-form';
import { TextAbstractControl } from '../abstract-control';
import DallasFormField from '../DallasFormField/DallasFormField';

interface Props<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
> extends Omit<TextAbstractControl<TName>, 'label' | 'labelId'> {
  label?: string;
  rows: 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
}

export default function DallasTextArea<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues>,
>({
  className,
  label = '',
  name,
  rows,
  placeholder,
  required,
  disabled,
  minLength,
  maxLength,
  errorPlacement,
  onChange: onValueChange,
}: Props<TFieldValues, TName>) {
  const {
    field: { ref, value, onChange: setValue, onBlur },
    fieldState: { error },
  } = useController({
    name,
    rules: {
      minLength,
      maxLength,
      required: required
        ? typeof required === 'string'
          ? required
          : 'Required field'
        : false,
    },
  });

  function onChange(value: string) {
    setValue(value);
    onValueChange?.(value);
  }

  return (
    <DallasFormField
      className={className}
      label={label}
      error={error}
      errorPlacement={errorPlacement}
      errorMessages={{
        minLength: minLength
          ? `At least ${minLength} characters long`
          : undefined,
        maxLength: maxLength
          ? `Maximum ${maxLength} characters long`
          : undefined,
      }}
      required={!!required}
    >
      <textarea
        ref={ref}
        name={name}
        placeholder={placeholder}
        onBlur={onBlur}
        value={value}
        onChange={({ target: { value } }) => onChange(value)}
        aria-invalid={!!error}
        disabled={disabled}
        rows={rows}
      />
    </DallasFormField>
  );
}
